import { useRef, useState, type ReactNode, type RefObject } from "react";
import { ModelElementType } from "@/components/entities";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { SpacePickerMenu } from "@/components/main/spaces/SpacePickerMenu";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { fetchMoveDestinations, moveModelToSpace } from "./ModelServices";

export interface MoveModelToSpace {
  isOpen: boolean;
  start: () => void;
  picker: ReactNode;
}

/// D65: "Move to space" — the picker lists only what the server says the move
/// door accepts, and the row moves optimistically, rolling back on refusal.
export function useMoveModelToSpace(
  model: ModelElementType,
  anchorRef: RefObject<HTMLElement | null>,
): MoveModelToSpace {
  const [isOpen, setIsOpen] = useState(false);
  const [eligible, setEligible] = useState<string[] | null>(null);
  const request = useRef(0);
  const patchModel = useMainStateStore((s) => s.patchModel);
  const spaceById = useOrgContextStore((s) => s.spaceById);

  const start = (): void => {
    const ticket = ++request.current;
    setEligible(null);
    setIsOpen(true);
    void fetchMoveDestinations(model.model_id).then((ids) => {
      if (ticket !== request.current) return;
      if (ids === null) {
        setIsOpen(false);
        showNotificationGlobal("Couldn't load the spaces to move to", "error");
        return;
      }
      setEligible(ids);
    });
  };

  const pick = (spaceId: string): void => {
    const rollback = patchModel(model.model_id, { space_id: spaceId });
    void moveModelToSpace(model.model_id, spaceId).then((outcome) => {
      if (outcome.ok) {
        const name = spaceById(spaceId)?.name;
        showNotificationGlobal(
          name ? `Moved to ${name}` : "Model moved",
          "success",
        );
      } else {
        rollback();
        showNotificationGlobal(outcome.message, "error");
      }
    });
  };

  const picker = (
    <SpacePickerMenu
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={() => {
        request.current++;
        setIsOpen(false);
      }}
      selectedSpaceId={null}
      onPick={pick}
      title="Move to"
      hint="Spaces you manage, excluding sealed spaces"
      eligibleSpaceIds={eligible}
      emptyLabel="No other space you manage"
    />
  );

  return { isOpen, start, picker };
}
